import { css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators';
import { commonFormStyles, commonPopupMobileStyle, commonPopupStyle } from '../../styles/common-style';
import { DeviceAwareComponent } from '../components/device-aware-component';
import { backArrowNavy } from '../icons/icons';
import { eventBus } from '@APP/utils/event-bus';
import { Events } from '@APP/events/app-events';
import { t } from '../../utils/locale';

/**
 * Intermediate "Get a call back" popup — opens when the customer picks
 * "Get a call back" from the Get Assistance dropdown, before the task is
 * created in Zoho. Captures an OPTIONAL free-text message so the customer
 * can tell the support team what's going on; the message travels through
 * to the Zoho Task via the ReturnBackToCustomerTask event →
 * questions-service → captain → zohoDataService.
 *
 * Styling reuses the funnel's shared layers rather than re-declaring:
 *   - commonPopupStyle   → h1 / h2 / p defaults, .grid-container, .col-*
 *   - commonFormStyles   → buttons.all (button[pop][primary|secondary])
 *                          + inputs.all (the shared <textarea> rules)
 * so the only local CSS here is layout glue specific to this popup.
 *
 * The illustration is NOT rendered here — it comes from popup-manager's
 * `map` registry (`{ name: 'get-a-call-back-popup', img: ErrorIllustration2 }`),
 * matching every other side-by-side popup in the funnel.
 *
 * Opened with technicalData `no-btns` so popup-manager suppresses its
 * standard next/support button rows; this popup owns the Request / Skip
 * pair. The top-right X close still comes from popup-manager's shell.
 *
 * Buttons:
 *   - ←  Back            → CloseCallBackForm — popup-manager restores the
 *                          source popup, no task is created.
 *   - X  Close (shell)   → PopupClose — everything dismisses, no task.
 *   - Skip               → ReturnBackToCustomerTask with no message.
 *   - Request call back  → ReturnBackToCustomerTask with the typed message.
 */
@customElement('get-a-call-back-popup')
export class GetACallBackPopup extends DeviceAwareComponent {
  // Max free-text length. Single source for the textarea's maxlength, the
  // counter, and the local truncation guard. Mirrored by a matching cap on
  // the captain route so a hostile client can't exceed it.
  private static readonly MAX_MESSAGE_LEN = 300;

  static get baseStyles() {
    return [
      commonPopupStyle,
      // Brings the shared button[pop][primary|secondary] and <textarea>
      // rules into this shadow root so the controls match the rest of the
      // funnel without re-declaring colors, radii, or font sizes.
      commonFormStyles,
      css`
        .header {
          display: flex;
          align-items: center;
          gap: 1rem;
        }
        .back-btn {
          background: none;
          border: none;
          padding: 0;
          margin: 0;
          min-height: unset;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          line-height: 0;
        }
        .back-btn:hover {
          opacity: 0.7;
        }
        /* backArrowNavy is a wide 20x16 glyph (shaft + head), so size by
           height and let width follow. 2.75rem matches the approved
           height; the arrow reads wider than a chevron at the same
           height because of the shaft. */
        .back-btn svg {
          height: 2.75rem;
          width: auto;
        }
        /* commonPopupStyle sets h1 to 3.33rem/400; this popup's heading is
           the focal point per the design, so bump size + weight. */
        h1.title {
          font-size: var(--font-size-h2, 4rem);
          font-weight: 700;
          margin: 0;
        }
        /* Matches the .message convention used by the other popups
           (before-exit-popup et al) — body copy constrained to the shared
           --message-max-width so lines wrap like the rest of the funnel. */
        .message {
          font-weight: 400;
          color: var(--c-text);
          max-width: var(--message-max-width, 39rem);
          margin-block-start: 1.5rem;
        }
        /* Field label sits above the textarea — black (not the navy
           --c-font-title used for headings) and a step up from the 2rem
           paragraph default so it reads as a form label, per the design. */
        .field-label {
          font-size: 2.25rem;
          font-weight: 700;
          color: var(--c-text);
          margin-block: 3rem 1rem;
        }
        /* The shared textarea rule sets height to 3x --input-height and
           resize:none; this popup wants a shorter box that the customer
           can grow, so override just those two. */
        textarea {
          height: calc(1.75 * var(--input-height));
          resize: vertical;
          border: var(--input-border-self);
        }
        textarea::placeholder {
          color: var(--c-text-understate);
        }
        .char-counter {
          display: block;
          text-align: end;
          /* --btn-note-text-size lives in tokens.ts, which this popup
             doesn't import; the fallback matches its value so the counter
             is sized correctly either way (inherited or not). */
          font-size: var(--btn-note-text-size, 2rem);
          color: var(--c-text-understate);
          margin-block-start: 0.5rem;
        }
        .actions {
          display: flex;
          gap: 2rem;
          align-items: center;
        }

        /* ---------------------------------------------------------------
           The funnel's button classes live in popup-manager's and
           lh-dropdown's shadow roots, so they can't reach this one.
           These are copied verbatim from those sources so the Request /
           Skip pair renders identically to the rest of the funnel:
             .popup-btn                     → popup-manager.ts
             .second-popup-btn              → popup-manager.ts (#second-popup-btn)
             .dropbtn                       → lh-dropdown.ts
           Keep in sync if the originals change.
           --------------------------------------------------------------- */
        .popup-btn {
          cursor: pointer;
          border: 2px solid var(--c-emphasize-sec);
          min-width: 20rem;
          margin: 2rem 0;
          text-align: center;
          font-size: 2.5rem;
          padding: 0 2rem;
          min-height: 6.25rem;
          width: max-content;
          place-self: var(--popup-place-self, end start);
        }

        .second-popup-btn {
          border: 2px solid var(--c-button-hover);
          color: var(--c-emphasize-sec);
          background-color: white;
          margin-right: 1rem;
          height: 6.25rem;
          padding: 0 2rem;
          font-size: 2.5rem;
          text-align: center;
        }


        /* Border matches the fill (like the funnel's #btn-next) so the
           filled pill shows no outline ring; .dropbtn:hover swaps both. */
        button.dropbtn {
          font-weight: 600;
          border: 2px solid var(--c-emphasize-sec);
          background: var(--btn-pri-bg);
          color: var(--btn-pri-text);
          border-radius: 5rem;
          padding-block: 1rem;
          display: flex;
          flex-direction: row;
          align-items: center;
          justify-content: center;
          cursor: pointer;
        }

        .dropbtn:hover {
          background-color: var(--c-button-hover);
          border: 2px solid var(--c-button-hover);
        }

        /* Skip is the outline variant: .popup-btn only, so it keeps the
           purple border but needs the pill radius + transparent fill that
           .dropbtn would otherwise have supplied. */
        .actions .popup-btn:not(.dropbtn) {
          border-radius: 5rem;
          background: transparent;
          color: var(--c-emphasize-sec);
        }

        .actions .popup-btn:not(.dropbtn):hover {
          background-color: var(--c-textbox-hover);
        }
      `,
    ];
  }

  static get desktopStyles() {
    return [css`
      h1.title {
        margin-block: 0;
      }
    `];
  }

  static get mobileStyles() {
    return [
      commonPopupMobileStyle,
      css`
        .actions {
          flex-direction: column-reverse;
          align-items: stretch;
          gap: 1.5rem;
        }
        .actions button {
          width: 100%;
        }
      `,
    ];
  }

  // Set by popup-manager via its unsafeHTML template — the popup the
  // customer was on before opening this form. Echoed back on
  // ReturnBackToCustomerTask so questions-service's errorCode derivation
  // resolves to the right task title. Empty string is valid (the Packages
  // screen path, which has no source popup).
  @property({ attribute: 'previous-popup-name', type: String })
    previousPopupName: string = '';

  // Kept in sync with the textarea for the counter + emit payload.
  @state()
  private _message: string = '';

  private _onMessageInput = (e: Event) => {
    const target = e.target as HTMLTextAreaElement;
    let value = target.value;
    if (value.length > GetACallBackPopup.MAX_MESSAGE_LEN) {
      // <textarea maxlength> is honoured inconsistently on paste across
      // browsers, so truncate here too rather than trusting the attribute.
      value = value.slice(0, GetACallBackPopup.MAX_MESSAGE_LEN);
      target.value = value;
    }
    this._message = value;
  };

  private _emitCloseForm = (submitted: boolean) => {
    eventBus().emit(Events.Type.CloseCallBackForm, { submitted });
  };

  private _onBack = () => {
    // Back arrow — no task, just restore the source popup.
    this._emitCloseForm(false);
  };

  private _emitReturnBackToCustomerTask = (includeMessage: boolean) => {
    const message = includeMessage ? this._message.trim() : '';
    eventBus().emit(Events.Type.ReturnBackToCustomerTask, {
      popupComponentName: this.previousPopupName || '',
      // Only send a non-empty message; presence of the field IS the
      // "customer added a note" flag downstream (no separate boolean).
      ...(message ? { message } : {}),
    });
    // Close after the task emit so popup-manager restores the source popup
    // before the async ReturnBackToCustomerTaskResponse confirmation lands.
    this._emitCloseForm(true);
  };

  private _onRequestCallBack = () => {
    this._emitReturnBackToCustomerTask(true);
  };

  private _onSkip = () => {
    this._emitReturnBackToCustomerTask(false);
  };

  private renderBody() {
    return html`
      <div class="header">
        <button class="back-btn" type="button" aria-label=${t('T_Back')} @click=${this._onBack}>
          ${backArrowNavy}
        </button>
        <h1 class="title">${t('T_Get_A_Call_Back_Title')}</h1>
      </div>
      <h2 class="message">${t('T_Get_A_Call_Back_Text')}</h2>
      <p class="field-label">${t('T_Get_A_Call_Back_Message_Label')}</p>
      <textarea
        maxlength="${GetACallBackPopup.MAX_MESSAGE_LEN}"
        placeholder=${t('T_Get_A_Call_Back_Message_Placeholder')}
        .value=${this._message}
        @input=${this._onMessageInput}
      ></textarea>
      <span class="char-counter">${this._message.length}/${GetACallBackPopup.MAX_MESSAGE_LEN}</span>
      <div class="actions">
        <button
          class="second-popup-btn dropbtn popup-btn"
          type="button"
          @click=${this._onRequestCallBack}
        >
          ${t('T_Get_A_Call_Back_Submit')}
        </button>
          
        <button class="popup-btn" type="button" style="min-width:14rem;font-weight: 600;" @click=${this._onSkip}>
          ${t('T_Get_A_Call_Back_Skip')}
        </button>
      </div>
    `;
  }

  renderMobileLayout() {
    return html`
      <div class="col-1">
        ${this.renderBody()}
      </div>
    `;
  }

  renderDesktopLayout() {
    return html`
      <div class="grid-container">
        <div class="col-1"></div>
        <div class="col-2">
          ${this.renderBody()}
        </div>
      </div>
    `;
  }

  renderContent() {
    return this.userDevice === 'desktop' ? this.renderDesktopLayout() : this.renderMobileLayout();
  }
}
